import { useState } from 'react'
import { useKaseyaGroups } from '../../hooks/api/useKaseyaGroups'
import { supabase } from '../../lib/supabase'
import { Select } from '../ui/Select'

interface Device {
  id: string
  name: string
  email: string
  deviceType: string
  status: string
  lastActive: string
}

interface DevicesTableWithBulkActionsProps {
  devices: Device[]
  companyId: string
  onDevicesUpdated?: () => void
}

export const DevicesTableWithBulkActions = ({
  devices,
  companyId,
  onDevicesUpdated
}: DevicesTableWithBulkActionsProps) => {
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set())
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedGroupId, setSelectedGroupId] = useState<string>('')
  const [isAssigning, setIsAssigning] = useState(false)

  const { data: kaseyaGroupsData } = useKaseyaGroups(companyId)
  const kaseyaGroups = kaseyaGroupsData?.groups || []

  const allSelected = devices.length > 0 && selectedDevices.size === devices.length

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedDevices(new Set())
    } else {
      setSelectedDevices(new Set(devices.map(d => d.id)))
    }
  }

  const handleSelectDevice = (deviceId: string) => {
    const newSelected = new Set(selectedDevices)
    if (newSelected.has(deviceId)) {
      newSelected.delete(deviceId)
    } else {
      newSelected.add(deviceId)
    }
    setSelectedDevices(newSelected)
  }

  const handleAssignToGroup = async () => {
    if (!selectedGroupId || selectedDevices.size === 0) return

    setIsAssigning(true)

    try {
      const deviceIds = Array.from(selectedDevices)
      const groupIdNum = parseInt(selectedGroupId)

      // Update all selected devices
      const { error } = await supabase
        .from('devices')
        .update({ kaseya_group_id: groupIdNum })
        .in('id', deviceIds)

      if (error) {
        console.error('Failed to assign devices:', error)
        alert('Failed to assign devices to group')
        return
      }

      // Clear selection and close modal
      setSelectedDevices(new Set())
      setShowAssignModal(false)
      setSelectedGroupId('')

      // Notify parent
      onDevicesUpdated?.()

      alert(`Successfully assigned ${deviceIds.length} device(s) to group`)
    } catch (err) {
      console.error('Error assigning devices:', err)
      alert('An unexpected error occurred')
    } finally {
      setIsAssigning(false)
    }
  }

  if (devices.length === 0) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-gray-200 py-12 text-center text-sm text-gray-500">
        No device data available for this organization.
      </div>
    )
  }

  return (
    <>
      {/* Bulk Action Bar */}
      {selectedDevices.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 border border-blue-200 px-4 py-3">
          <span className="text-sm font-medium text-blue-900">
            {selectedDevices.size} device(s) selected
          </span>
          <button
            onClick={() => setShowAssignModal(true)}
            className="rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            Assign to Group
          </button>
        </div>
      )}

      {/* Devices Table */}
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Device
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Owner
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Last Active
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white text-sm text-gray-600">
            {devices.map(device => (
              <tr
                key={device.id}
                className={selectedDevices.has(device.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedDevices.has(device.id)}
                    onChange={() => handleSelectDevice(device.id)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </td>
                <td className="px-4 py-3 text-gray-900">{device.name}</td>
                <td className="px-4 py-3">{device.email}</td>
                <td className="px-4 py-3">{device.deviceType}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                      device.status === 'online'
                        ? 'bg-green-100 text-green-700'
                        : device.status === 'offline'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {device.status}
                  </span>
                </td>
                <td className="px-4 py-3">{device.lastActive}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assign to Group Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowAssignModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Assign Devices to Group
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a Kaseya group to assign {selectedDevices.size} device(s) to:
            </p>

            <Select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="w-full mb-4"
            >
              <option value="">Select Group</option>
              {kaseyaGroups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignToGroup}
                disabled={!selectedGroupId || isAssigning}
                className="flex-1 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAssigning ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
